import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Loader2, Check, XCircle, Pause, Play, ChevronDown, ChevronUp, Plane,
  Mail, Phone, MapPin, Radar, ShieldCheck, Calendar, StickyNote, Send,
} from "lucide-react";

const STATUS_BADGE = {
  pending: "bg-amber-50 text-amber-600", approved: "bg-green-50 text-green-600",
  suspended: "bg-slate-100 text-slate-500", rejected: "bg-red-50 text-red-600",
};
const TYPE_LABEL = { drone_pilot: "Drone Pilot (Part 107)", investigator: "Field Investigator", photographer: "Scene Photographer", other: "Other" };
const INS_LABEL = { have_liability: "Liability", have_hull: "Hull + Liability", none_yet: "Not provided yet", expired: "Expired / renewing" };
const FILTERS = ["pending", "approved", "suspended", "rejected", "all"];

function Detail({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
      <div className="min-w-0"><div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div><div className="text-sm text-navy truncate">{value || "—"}</div></div>
    </div>
  );
}

function PilotCard({ p, onAction, onNote }) {
  const [open, setOpen] = useState(p.status === "pending");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const st = p.status;

  const doAction = async (status) => {
    let reason = null;
    if (status === "rejected") { reason = window.prompt("Rejection reason (optional, shared with the pilot):") || undefined; }
    setBusy(true); await onAction(p.pilot_id, status, reason); setBusy(false);
  };
  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true); await onNote(p.pilot_id, note.trim()); setNote(""); setBusy(false);
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden" data-testid={`pilot-app-${p.pilot_id}`}>
      <div className="p-4 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="h-10 w-10 rounded-xl bg-cobalt-light flex items-center justify-center shrink-0"><Plane className="w-5 h-5 text-cobalt" /></div>
          <div className="min-w-0">
            <div className="font-semibold text-navy truncate">{p.name || "Unnamed pilot"}</div>
            <div className="text-xs text-slate-500 truncate">{p.email} · {TYPE_LABEL[p.provider_type] || p.provider_type || "Pilot"}</div>
            <div className="text-xs text-slate-400 mt-0.5">{p.city ? `${p.city}, ` : ""}{p.state || "—"} · {p.service_radius_mi || "—"}mi · applied {p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${STATUS_BADGE[st] || "bg-slate-100 text-slate-500"}`} data-testid={`pilot-status-${p.pilot_id}`}>{st === "approved" ? `active · ${p.availability}` : st}</span>
          <button onClick={() => setOpen((o) => !o)} data-testid={`pilot-view-${p.pilot_id}`} className="p-2 rounded-full hover:bg-slate-100 text-slate-500">{open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</button>
        </div>
      </div>

      {open && (
        <div className="px-4 pb-4 border-t border-slate-50 pt-4" data-testid={`pilot-detail-${p.pilot_id}`}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Detail icon={Mail} label="Email" value={p.email} />
            <Detail icon={Phone} label="Phone" value={p.phone} />
            <Detail icon={MapPin} label="Service area" value={[p.city, p.state].filter(Boolean).join(", ")} />
            <Detail icon={Radar} label="Response type" value={TYPE_LABEL[p.provider_type] || p.provider_type} />
            <Detail icon={ShieldCheck} label="FAA Part 107 #" value={p.faa_cert} />
            <Detail icon={ShieldCheck} label="Insurance" value={INS_LABEL[p.insurance_status] || p.insurance_status} />
            <Detail icon={MapPin} label="Availability radius" value={p.service_radius_mi ? `${p.service_radius_mi} mi` : "—"} />
            <Detail icon={Plane} label="Equipment" value={p.equipment} />
            <Detail icon={Calendar} label="Signed up" value={p.created_at ? new Date(p.created_at).toLocaleString() : "—"} />
          </div>
          {p.rejection_reason && st === "rejected" && <div className="mt-3 text-xs text-red-500">Rejection reason: {p.rejection_reason}</div>}

          {/* Admin notes */}
          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-500 flex items-center gap-1.5 mb-1.5"><StickyNote className="w-3.5 h-3.5" /> Internal admin notes</div>
            {(p.admin_notes || []).length > 0 && (
              <div className="space-y-1.5 mb-2">
                {p.admin_notes.map((n, i) => (
                  <div key={i} className="text-xs bg-slate-50 rounded-lg px-3 py-2 text-slate-600" data-testid={`pilot-note-${p.pilot_id}-${i}`}>{n.note} <span className="text-slate-400">— {n.author}, {new Date(n.at).toLocaleDateString()}</span></div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note…" data-testid={`pilot-note-input-${p.pilot_id}`} className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-cobalt" />
              <button onClick={addNote} disabled={busy || !note.trim()} data-testid={`pilot-note-add-${p.pilot_id}`} className="px-3 py-2 rounded-xl bg-slate-800 text-white text-sm disabled:opacity-50"><Send className="w-4 h-4" /></button>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            {st === "pending" && <>
              <button onClick={() => doAction("approved")} disabled={busy} data-testid={`approve-pilot-${p.pilot_id}`} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-cobalt text-white text-sm font-medium hover:bg-cobalt-hover disabled:opacity-50"><Check className="w-4 h-4" /> Approve</button>
              <button onClick={() => doAction("rejected")} disabled={busy} data-testid={`reject-pilot-${p.pilot_id}`} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-600 text-sm hover:bg-red-50 hover:text-red-600 disabled:opacity-50"><XCircle className="w-4 h-4" /> Reject</button>
            </>}
            {st === "approved" && <button onClick={() => doAction("suspended")} disabled={busy} data-testid={`suspend-pilot-${p.pilot_id}`} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 text-sm font-medium hover:bg-red-50 hover:text-red-600 disabled:opacity-50"><Pause className="w-4 h-4" /> Suspend</button>}
            {(st === "suspended" || st === "rejected") && <button onClick={() => doAction("approved")} disabled={busy} data-testid={`activate-pilot-${p.pilot_id}`} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-cobalt text-white text-sm font-medium hover:bg-cobalt-hover disabled:opacity-50"><Play className="w-4 h-4" /> Reactivate</button>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminPilotApplications() {
  const [pilots, setPilots] = useState(null);
  const [filter, setFilter] = useState("pending");

  const load = () => api.get("/admin/pilots").then(({ data }) => setPilots(data)).catch(() => setPilots([]));
  useEffect(() => { load(); }, []);

  const onAction = async (id, status, reason) => {
    try { await api.put(`/admin/pilots/${id}/status`, { status, reason }); toast.success(`Pilot ${status === "approved" ? "activated" : status}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Action failed"); }
  };
  const onNote = async (id, note) => {
    try { await api.post(`/admin/pilots/${id}/note`, { note }); toast.success("Note added"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
  };

  if (pilots === null) return <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 text-cobalt animate-spin" /></div>;

  const counts = pilots.reduce((a, p) => ((a[p.status] = (a[p.status] || 0) + 1), a), {});
  const shown = filter === "all" ? pilots : pilots.filter((p) => p.status === filter);

  return (
    <div data-testid="admin-pilot-applications">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="font-heading font-semibold text-navy flex items-center gap-2">
          Pilot Applications
          {counts.pending > 0 && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full" data-testid="pending-pilots-count">{counts.pending} pending</span>}
        </h3>
      </div>
      <div className="flex gap-1.5 flex-wrap mb-4">
        {FILTERS.map((fkey) => (
          <button key={fkey} onClick={() => setFilter(fkey)} data-testid={`pilot-filter-${fkey}`}
            className={`text-xs font-medium px-3 py-1.5 rounded-full capitalize transition-colors ${filter === fkey ? "bg-navy text-white" : "bg-slate-100 text-slate-500 hover:text-navy"}`}>
            {fkey === "approved" ? "active" : fkey}{fkey !== "all" && counts[fkey] ? ` (${counts[fkey]})` : ""}
          </button>
        ))}
      </div>
      {shown.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center text-slate-500" data-testid="no-pilots">
          {filter === "pending" ? "No pending pilot applications right now." : `No ${filter} pilots.`}
        </div>
      ) : (
        <div className="space-y-3">{shown.map((p) => <PilotCard key={p.pilot_id} p={p} onAction={onAction} onNote={onNote} />)}</div>
      )}
    </div>
  );
}
